import {
  UserTypeEnum,
  type UserUnion,
  type User,
  type Pupil,
  type Visitor,
  type Group,
} from "@/services/orval/internalApiV1";
import { defineStore } from "pinia";

type UserStoreType = {
  user?: UserUnion;
  accessToken?: string;
  publicToken?: string;
  visitorToken?: string;
  itemPassword?: string;
  itemPasswordRequired: boolean;
  itemPasswordErrorCount: number;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isPupil = (user?: any): user is Pupil => {
  return (user as UserUnion)?.type === UserTypeEnum.Pupil;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isUser = (user?: any): user is User => {
  return (user as UserUnion)?.type === UserTypeEnum.User;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isVisitor = (user?: any): user is Visitor => {
  return (user as UserUnion)?.type === UserTypeEnum.Visitor;
};

export const useUserStore = defineStore("user", {
  state: (): UserStoreType => {
    return {
      user: undefined,
      accessToken: undefined,
      publicToken: undefined,
      visitorToken: undefined,
      itemPassword: undefined,
      itemPasswordRequired: false,
      itemPasswordErrorCount: 0,
    };
  },
  getters: {
    isAuthenticated(state) {
      return isUser(state.user) || isPupil(state.user);
    },
    isAdmin(state) {
      return isUser(state.user) && state.user.admin;
    },
    isVisitor(state) {
      return isVisitor(state.user);
    },
    isIncognito(state) {
      return state.user?.incognito;
    },
    isPupil(state) {
      return isPupil(state.user);
    },
    isUser(state) {
      return isUser(state.user);
    },
    firstname(state) {
      if (isUser(state.user)) {
        return state.user?.firstname;
      }
    },
    hasUnreadPosts(state) {
      return isUser(state.user) ? state.user?.hasUnreadPosts : undefined;
    },
    isPlatformReviewer(state) {
      return isUser(state.user) ? state.user?.reviewer : undefined;
    },
    additionalQualificationGroup(state) {
      return isUser(state.user)
        ? state.user?.additionalQualificationGroup
        : undefined;
    },
    additionalQualificationUrl() {
      if (this.additionalQualificationGroup?.additionalQualification) {
        return (this.additionalQualificationGroup as unknown as Group).url;
      }

      return undefined;
    },
    disabledTools(state) {
      return isUser(state.user) ? state.user?.disabledTools : [];
    },
    disabledToolsFeatures(state) {
      return isUser(state.user) ? state.user?.disabledToolsFeatures : [];
    },
    isDisabledTool(state) {
      return (tool: string) => {
        return isUser(state.user)
          ? state.user?.disabledTools?.includes(tool)
          : false;
      };
    },
    isDisabledToolsFeature(state) {
      return (feature: string) => {
        return (
          isUser(state.user) &&
          state.user?.disabledToolsFeatures?.includes(feature)
        );
      };
    },
    name(state) {
      if (isPupil(state.user)) {
        return state.user?.name;
      } else if (isUser(state.user)) {
        return state.user?.displayName;
      }

      return "";
    },
    shortName(): string {
      if (!this.name) {
        return "";
      }

      return this.name.substring(0, 30);
    },
    isFeatureEnabled(state) {
      return (feature: string) => {
        return state.user?.enabledFeatures?.includes(feature);
      };
    },
  },
  actions: {
    reset() {
      this.user = undefined;
      this.accessToken = undefined;
      this.publicToken = undefined;
      this.itemPassword = undefined;
      this.itemPasswordRequired = false;
      this.itemPasswordErrorCount = 0;
    },
    clearPassword() {
      this.itemPassword = undefined;
    },
  },
});
